// "Money flies into Balance" — the tiny visual that runs when a talent taps
// the $ (Play) button on a queued card. A copy of the pill lifts off the
// button, dives into the Balance tab (or the header's Balance link on desktop
// web), and the target pulses green for half a second. Pure decoration: it
// never touches the play/capture request, never blocks it, and never asserts
// an amount beyond the label already on the button.
//
// Targets are any element carrying `data-money-target` — the first one that
// is actually laid out (non-zero rect) wins, so the phone tab bar and the
// desktop header link can both carry the attribute without coordination.
import "./flyToBalance.css";

const RECEIVE_CLASS = "money-receive";
const FLIGHT_MS = 520;
const RECEIVE_MS = 640;

function findTarget(): HTMLElement | null {
  const all = Array.from(document.querySelectorAll<HTMLElement>("[data-money-target]"));
  return all.find((el) => el.getBoundingClientRect().width > 0) || null;
}

function pulse(target: HTMLElement) {
  target.classList.remove(RECEIVE_CLASS);
  // Force a reflow so a second flight re-triggers the CSS animation.
  void target.offsetWidth;
  target.classList.add(RECEIVE_CLASS);
  window.setTimeout(() => target.classList.remove(RECEIVE_CLASS), RECEIVE_MS);
}

/** The landing pulse alone, no flight — the tab bar's "money is waiting" nudge. */
export function pulseBalanceTarget(): void {
  const target = findTarget();
  if (target) pulse(target);
}

/**
 * Fly `label` from `fromEl` into the Balance target. Resolves when the pulse
 * has been triggered (immediately when there is no target, or when the user
 * prefers reduced motion — then only the pulse runs).
 */
export function flyMoneyToBalance(fromEl: HTMLElement, label: string): void {
  const target = findTarget();
  if (!target) return;

  const reduceMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduceMotion || typeof fromEl.animate !== "function") {
    pulse(target);
    return;
  }

  // Aim for the icon when the target has one (tab bar), else the element.
  const aim = (target.querySelector(".tabbar-ic") as HTMLElement | null) || target;
  const from = fromEl.getBoundingClientRect();
  const to = aim.getBoundingClientRect();

  const fly = document.createElement("div");
  fly.className = "money-flyer";
  fly.textContent = label;
  fly.setAttribute("aria-hidden", "true");
  Object.assign(fly.style, {
    left: `${from.left}px`,
    top: `${from.top}px`,
    width: `${from.width}px`,
    height: `${from.height}px`,
  });
  document.body.appendChild(fly);

  const dx = to.left + to.width / 2 - (from.left + from.width / 2);
  const dy = to.top + to.height / 2 - (from.top + from.height / 2);

  const anim = fly.animate(
    [
      { transform: "translate(0,0) scale(1)", opacity: 1, easing: "cubic-bezier(.2,.7,.4,1)" },
      // A real lift first (22px up, whichever way the target lies), then the dive.
      {
        transform: `translate(${dx * 0.22}px, -22px) scale(1.04)`,
        opacity: 1,
        offset: 0.3,
        easing: "cubic-bezier(.55,0,.85,.4)",
      },
      { transform: `translate(${dx}px, ${dy}px) scale(0.32)`, opacity: 0.25 },
    ],
    { duration: FLIGHT_MS, fill: "forwards" }
  );

  let landed = false;
  const land = () => {
    if (landed) return;
    landed = true;
    fly.remove();
    pulse(target);
  };
  anim.onfinish = land;
  anim.oncancel = () => fly.remove();
  // Belt and braces: if the page is backgrounded mid-flight the animation
  // clock may stall — never leave a stray pill on screen.
  window.setTimeout(land, FLIGHT_MS + 400);
}
